/**
 * SignaturePad — a lightweight canvas-based signature capture control.
 * (2026-06-11, Jason Cox) New for Manual Checkout: replaces the typed-initials
 * field with a drawn signature. Emits a PNG data URL via onChange; the parent
 * sends that to the backend, which uploads it to S3.
 *
 * Pointer events cover mouse, touch, and stylus with one code path. `touch-action:
 * none` on the canvas stops the browser from scrolling/zooming while the user
 * signs. The backing store is sized to the device pixel ratio so strokes stay
 * crisp on high-DPI screens.
 *
 * Controlled-ish: the component owns the canvas pixels, but when `value` is
 * cleared by the parent (e.g. after a successful checkout) the canvas is wiped
 * to stay in sync. We intentionally do NOT re-render an externally supplied
 * value back onto the canvas — this control is for capture, not display.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';

interface SignaturePadProps {
  /** Current value (PNG data URL) or empty/null when no signature is captured. */
  value: string | null;
  /** Called with the PNG data URL after each stroke, or null when cleared. */
  onChange: (dataUrl: string | null) => void;
  /** When true, render the error border treatment (mirrors `.input-error`). */
  error?: boolean;
  /** CSS height of the drawing surface in pixels. Defaults to 160. */
  height?: number;
  /** Optional wrapper className. */
  className?: string;
}

const PEN_COLOR = '#111827'; // secondary-900-ish; clearly visible on white
const PEN_WIDTH = 2.5;

const SignaturePad: React.FC<SignaturePadProps> = ({
  value,
  onChange,
  error = false,
  height = 160,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  /** Size the canvas backing store to its CSS box × devicePixelRatio. */
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    // Guard against a zero-width measurement (e.g. canvas momentarily hidden).
    const cssWidth = rect.width || canvas.clientWidth || 300;
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = PEN_WIDTH;
    ctx.strokeStyle = PEN_COLOR;
  }, [height]);

  useEffect(() => {
    setupCanvas();
    window.addEventListener('resize', setupCanvas);
    return () => window.removeEventListener('resize', setupCanvas);
  }, [setupCanvas]);

  /** Wipe the canvas when the parent clears the value (e.g. after submit). */
  useEffect(() => {
    // Reason: only react to the value going empty; drawing updates flow the
    // other direction (canvas -> onChange) so we don't re-render value back in.
    if (!value && hasInkRef.current) {
      clearCanvas();
    }
  }, [value]);

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(e);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const last = lastPointRef.current;
    if (!canvas || !ctx || !last) return;
    const p = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    hasInkRef.current = true;
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
    if (hasInkRef.current) {
      onChange(canvas.toDataURL('image/png'));
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      // clearRect uses the untransformed backing-store size.
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    hasInkRef.current = false;
    onChange(null);
  };

  return (
    <div className={className}>
      <div
        className={`relative rounded-lg border bg-white ${
          error ? 'border-error-300' : 'border-secondary-300'
        }`}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-label="Signature drawing area"
          data-testid="signature-canvas"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
          className="block w-full rounded-lg cursor-crosshair touch-none"
          style={{ height }}
        />
        {!value && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-secondary-400 select-none">
            Sign here
          </span>
        )}
        <button
          type="button"
          onClick={clearCanvas}
          className="absolute top-1 right-1 text-secondary-400 hover:text-error-600"
          aria-label="Clear signature"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
